import { streamRequest, postRequest, getRequest } from './api';

/**
 * Manager Service API
 */

/**
 * Stream chat with manager agent
 * @param {string} question - User question
 * @param {Array} conversationHistory - Previous conversation messages
 * @param {Function} onMessage - Callback for each SSE message
 * @param {Function} onError - Callback for errors
 * @param {Function} onComplete - Callback when stream completes
 * @param {string} modelKey - Optional model key to use
 */
export async function streamManagerChat(question, conversationHistory, onMessage, onError, onComplete, modelKey = null, _userEmail = null, _userProfile = null, sessionId = null) {
  // _userEmail and _userProfile parameters are kept for call-site compatibility
  // but ignored — the server now sources identity from the JWT.
  const payload = {
    message: question,
    conversation_history: conversationHistory
  };
  if (modelKey) payload.model_key = modelKey;
  if (sessionId) payload.session_id = sessionId;
  await streamRequest('manager', '/chat', payload, onMessage, onError, onComplete);
}

/**
 * Save Q&A to history
 * @param {string} question - User question
 * @param {string} answer - Assistant answer
 * @param {object} metadata - Additional metadata
 * @returns {Promise<object>} Response data
 */
export async function saveQA(question, answer, metadata = {}) {
  return await postRequest('manager', '/qa/save', { question, answer, ...metadata });
}

/**
 * Submit feedback for a Q&A
 * @param {string} qaId - Q&A ID
 * @param {number} rating - Rating (1-5)
 * @param {string} comment - Optional comment
 * @returns {Promise<object>} Response data
 */
export async function submitFeedback(qaId, rating, comment = '') {
  return await postRequest('manager', '/qa/feedback', { qa_id: qaId, rating, comment });
}

/**
 * Get Q&A history
 * @param {object} filters - Filter parameters
 * @returns {Promise<object>} Response data with history items
 */
export async function getQAHistory(filters = {}) {
  return await getRequest('manager', '/qa/history', filters);
}

/**
 * Get Q&A statistics
 * @returns {Promise<object>} Response data with statistics
 */
export async function getQAStatistics() {
  return await getRequest('manager', '/qa/statistics');
}

/**
 * Mark Q&A answer as correct or incorrect
 * @param {number} qaId - Q&A ID
 * @param {boolean} isCorrect - True if correct, false if incorrect
 * @param {boolean} addToGallery - Add to gallery if correct (default: true)
 * @returns {Promise<object>} Response data
 */
export async function markQAAnswer(qaId, isCorrect, addToGallery = true) {
  return await postRequest('manager', '/qa/mark', { 
    qa_id: qaId, 
    is_correct: isCorrect,
    add_to_gallery: addToGallery
  });
}

/**
 * Get agent information (system prompt and tools)
 * @returns {Promise<object>} Response data with agent info
 */
export async function getAgentInfo() {
  return await getRequest('manager', '/agent/info');
}

/**
 * Get available AI models
 * @returns {Promise<object>} Response data with models list
 */
export async function getAvailableModels() {
  return await getRequest('manager', '/models');
}

export default {
  streamManagerChat,
  saveQA,
  submitFeedback,
  getQAHistory,
  getQAStatistics,
  markQAAnswer,
  getAgentInfo,
  getAvailableModels
};
